/** @jsxImportSource @emotion/react */
import React, { ReactElement, useEffect, useMemo, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { Button } from '@mui/material';
import dayjs from 'dayjs';
import { useLoading } from 'components/process/Loading';
import { getUserInfo } from 'apis/common/Employee';
import { useCommonModal } from 'hooks/useCommonModal';
import { useMessageBar } from 'hooks/useMessageBar';
import {
  approveApprovalRequestResult,
  cancelApprovalRequestResult,
  findApproveRequestDetail,
  findAprReqTplInfo,
  getMasterId,
  rejectApprovalRequestResult,
  removeApprovalRequestResult,
  rewriteApprovalRequestResult,
  saveApproveRequest,
} from 'apis/approves/approves';
import {
  RequestDetail,
  SaveApprover,
  adapterApproveToEmp,
  ApprovalAppd,
  clearApprovalAppd,
  ApproveRequestDetail,
} from 'models/approves/ReferenceOpinion';
import ApproveBasicInfo from './ApproveBasicInfo';
import ApprovalLineInfo from './ApproveLineInfo';
import ApproveRequestDetailCase, { ApproveRequestPageType } from './ApproveRequestDetailCase';
import { IconButton } from 'components/buttons/IconSVG';
import CustomDialog from 'components/modals/common/CustomDialog';
import useSessionStore from 'stores/useSessionStore';
import ApproveModal from 'components/modals/approves/ApproveModal';
import ApproveDetailLineInfo from './ApproveDetailLineInfo';
import ApproveDetailBasicInfo from './ApproveDetailBasicInfo';
import _ from 'lodash';
import { Employee } from '../../models/admin/Employee';
import ApproveSendEmailModal from 'components/modals/common/ApproveSendEmailModal';
import { GlobalBtnGroup } from 'components/layouts/ContentLayout';

type Props = {
  open: boolean;
  close: () => void;
  pageId: ApproveRequestPageType;
  aprReqId?: string;
  condition?: any;
};

/**
 * @desc 결재 요청 팝업
 * @author
 * -------------------------------------------------------------------------------------
 *  Date.       author         Description
 * -------------------------------------------------------------------------------------
 * 2025.10.31   jshwang12     업무화면에서 넘어오는 결재자 정보가 있는 경우 추가 (셋업일정요청의 경우 결재자 수정 불가)
 * 2025.11.05   jshwang12     FA요청인 경우 전체 병렬지정 가능
 * 2025.11.07   shkim0407     협력사 권한 요청의 경우 결재자 셋팅 추가
 * 2025.11.23   shkim0407     협력사 권한 요청의 경우 벨리데이션 추가
 * 2025.12.09   jshwang12     FA요청 결재취소인 경우 임시저장 상태로 변경 (현재 결재ID 유지), 재작성/결재취소 시 화면 재조회 처리
 * 2026.01.20   jihwan89      CSR E20260108-002100 결재문서 임시저장(TMP)/상신취소(CCL)가 아닌 상태의 결재 공유메일 추가
 */
const ApproveRequestModal = (props: Props) => {
  const { pageId } = props;
  const { t } = useTranslation();
  const { openCommonModal } = useCommonModal();
  const { openMessageBar } = useMessageBar();
  const { userId } = useSessionStore();
  const { openLoading } = useLoading();
  const basicRef = useRef<any>();
  const childRef = useRef<any>(null);
  const [detail, setDetail] = useState<RequestDetail>({
    aprPvnDdlnCd: 'APPRCODE_5Y', // 결재보존기한코드
    // apprSecurityType: '0', // 문서종류 (비밀문서:1,일반문서:0)
    apprLineType: '0', // 협의유형 (0:순차협의, 1:병렬협의)
    aprPageId: pageId,
  });
  const [approveLine, setApproveLine] = useState({
    approvalAppd: [] as ApprovalAppd[],
    approvalInfr: [] as ApprovalAppd[],
  });
  const approvalsRef = useRef<SaveApprover[]>([]);
  const notifiersRef = useRef<SaveApprover[]>([]);
  const [condition, setCondition] = useState<any>(props.condition);
  const [aprReqId, setAprReqId] = useState<string>(props.aprReqId || '');
  const [waitApprovalAppd, setWaitApprovalAppd] = useState<ApprovalAppd>(clearApprovalAppd);
  const [isApprover, setIsApprover] = useState<boolean>(false);
  const [isNewApprove, setNewApprove] = useState<boolean>(false);
  const [approveModalOpen, setApproveModalOpen] = useState<boolean | 'APPROVE' | 'REJECT'>(false);

  const [isOpenEmailModal, setOpenEmailModal] = useState<boolean>(false);
  const [getConditionSendMail, setConditionSendMail] = useState<any>();

  const isEditable = useMemo(
    () => isNewApprove || 'TMP' === detail?.aprReqProgStatCd,
    [isNewApprove, detail?.aprReqProgStatCd]
  );
  const isSetupRequest = useMemo(
    () =>
      [
        ApproveRequestPageType.SETUP_SCHD_REG,
        ApproveRequestPageType.SETUP_SCHD_CHG,
        ApproveRequestPageType.EXCP_AUTH_REQ,
      ].includes(pageId),
    [pageId]
  );
  const REWRITE_ALLOWED_PAGE_IDS = [
    //재작성 버튼 표시할 페이지들
    ApproveRequestPageType.SETUP_SCHD_REG,
    ApproveRequestPageType.SETUP_SCHD_CHG,
    ApproveRequestPageType.EXCP_AUTH_REQ,
  ];

  const DELETE_ALLOWED_PAGE_IDS = [
    //삭제 버튼 표시할 페이지들
    ApproveRequestPageType.SETUP_SCHD_REG,
    ApproveRequestPageType.SETUP_SCHD_CHG,
    ApproveRequestPageType.EXCP_AUTH_REQ,
  ];

  /* [25.11.07] 협력사 권한 요청 관련 결재자 셋팅 부분 */
  const [approvalPrj, setApprovalPrj] = useState<any[]>();
  const [approvalRole, setApprovalRole] = useState<any[]>();

  const init = () => {
    if (aprReqId) {
      searchRequest();
    } else {
      newRequest();
    }
  };

  useEffect(() => {
    openLoading(true);
    init();
    openLoading(false);
  }, []);

  const newRequest = async () => {
    const result = await getMasterId();
    setNewApprove(true);
    if (!result.data) {
      openMessageBar({
        type: 'error',
        content: t('com.msg.결재ID 생성에 실패했습니다.', '결재ID 생성에 실패했습니다.'),
      });
      handleClose();
      return;
    }

    const userInfo = await getUserInfo();
    setAprReqId(result.data ?? '');

    // 양식으로 조회

    const aprTplInfo = await findAprReqTplInfo(props.pageId);

    setDetail({
      ...detail,
      aprReqId: result.data ?? '',
      aprReqUserNm: userInfo,
      aprTplNm: aprTplInfo.aprTplNm,
      aprReqTitNm: aprTplInfo.prsDesc,
      trnmBdyCtn: aprTplInfo.wcstDesc,
      apprLineType: aprTplInfo.apprLineType,
      dataInsDtm: dayjs().format('YYYY.MM.DD').toString(),
    });

    // 업무페이지에서 넘어오는 통보처나 결재자가 있는 경우(결재통보처가 아닌 별도 통보처사용하는 경우)
    if (
      (!_.isNull(props.condition.approvalInfr) && !_.isEmpty(props.condition.approvalInfr)) ||
      (props?.condition?.approvalAppd || []).length
    ) {
      approvalsRef.current = (props.condition.approvalAppd || []).map((o) => {
        return {
          ...o,
          aprAprvUserId: o.aprAprvUserId || o.userId,
        } as SaveApprover;
      });
      notifiersRef.current = props.condition.approvalInfr;
      setApproveLine({
        approvalAppd: props.condition.approvalAppd,
        approvalInfr: props.condition.approvalInfr,
      });
    } else {
      const saveApprover = (aprTplInfo.aprLine || []).map((o) => {
        return {
          ...o,
          status: o.inoStatCd, // 결재
          aprAprvUserId: o.userId,
          aprTpDivsCd: o.aprTpDivsCd,
          grnrAprTpDivsCd: o.aprTpDivsCd, // 결재 0 , 합의 6 - 결재 고정이지만 병렬 Y로 선택할경우 합의 6으로 변경됨.(서비스에서변경)
          prlYn: o.prlYn, // 병렬여부
        } as SaveApprover;
      });

      const ntdkApprover = (aprTplInfo.ntdkLine || []).map((o) => {
        return {
          ...o,
          status: 'C', // 통보
          aprAprvUserId: o.userId,
          grnrAprTpDivsCd: 'C', // 통보
          aprvUserNm: o.empNm,
          aprvUserJikwiNm: o.jtiNm,
          aprvUserDeptNm: o.deptNm,
          deptNm: o.deptNm,
          empNm: o.empNm,
          jtiNm: o.jtiNm,
        } as SaveApprover;
      });

      approvalsRef.current = saveApprover;
      notifiersRef.current = ntdkApprover;
    }
  };

  const searchRequest = async (pAprReqId?: string) => {
    // [25.12.09] 결재 재작성 요청 후 새로 생성된 결재ID 기준으로 화면 재조회하기 위해 pAprReqId 추가
    const response = await findApproveRequestDetail(pAprReqId || aprReqId);
    if (!response) {
      openMessageBar({
        type: 'error',
        content: t('com.msg.결재 정보가 없습니다.', '결재 정보가 없습니다.'),
      });
      handleClose();
      return;
    }
    setNewApprove(false);
    const approvalAppd = adapterApproveToEmp(response.approvalAppd); // 결재자
    const approvalInfr = adapterApproveToEmp(response.approvalInfr); // 통보자
    setDetail({
      ...response,
      approvalAppd: approvalAppd,
      approvalInfr: approvalInfr,
    });
    if ('condition' in response && response?.condition && Object.keys(response?.condition).length) {
      setCondition(response?.condition);
    }
    approvalsRef.current = approvalAppd;
    notifiersRef.current = approvalInfr;
    setApproveLine({
      approvalAppd: response.approvalAppd,
      approvalInfr: response.approvalInfr,
    });
    const nowApprove = response.approvalAppd?.find((item) => item.aprPsgStatCd == 'WAIT');
    if (nowApprove && nowApprove.aprAprvUserId === userId) {
      setWaitApprovalAppd(nowApprove);
      setIsApprover(true);
    } else {
      // [고도화] 병렬결재 처리
      // 병렬은 snb 번호가 동일함  병렬결재 대상인 경우 승인 활성화 하도록 변경.
      if (nowApprove && nowApprove.prlYn === 'Y') {
        const nextApprove = response.approvalAppd?.find(
          (o) => nowApprove.aprLnSnb == o.aprLnSnb && o.aprAprvUserId == userId
        );
        if (nextApprove) {
          setWaitApprovalAppd(nextApprove);
          setIsApprover(true);
        }
      }
    }
  };

  const validation = async () => {
    if (!(await basicRef.current?.validate())) return false;
    if (!(await childRef.current?.validate())) return false;

    /* [25.11.23] 협력사 권한 요청의 경우 벨리데이션 */
    if (isSetupRequest && props.pageId == 'EXCP_AUTH_REQ') {
      if (childRef.current?.getBizReqData(detail.aprReqId).requestUserList.length == 0) {
        openMessageBar({
          type: 'error',
          content: t(
            'com.msg.신청 인원은 1명 이상 선택 되어야 합니다.',
            '신청 인원은 1명 이상 선택 되어야 합니다.'
          ),
        });
        return false;
      }
    }

    if (!approvalsRef.current?.length) {
      openMessageBar({
        type: 'error',
        content: t('com.msg.승인자 정보를 추가해 주세요.', '승인자 정보를 추가해 주세요.'),
      });
      return false;
    }

    // [25.11.05] FA요청인 경우 전체 병렬지정 가능
    if (approvalsRef.current[approvalsRef.current.length - 1].prlYn !== 'N' && !isSetupRequest) {
      openMessageBar({
        type: 'error',
        content: t(
          'com.msg.마지막결재자는 병렬지정할수없습니다.',
          '마지막결재자는 병렬지정할수없습니다.'
        ),
      });
      return false;
    }

    return true;
  };

  /**
   * 결재 요청 처리
   */
  const handleConfirm = async () => {
    if (!(await validation())) return;

    const content = basicRef.current?.getReqContent();
    console.log(content);
    console.log(approvalsRef.current);

    openCommonModal({
      modalType: 'confirm',
      content: t('com.msg.결재요청하시겠습니까?', '결재요청하시겠습니까?'),
      yesCallback: async () => {
        openLoading(true);
        const response = await saveApproveRequest({
          ...detail,
          trnmBdyCtn: content,
          approvalAppd: approvalsRef.current,
          approvalInfr: notifiersRef.current,
          aprReqProgStatCd: 'REQ',
          optValCtn1: pageId, // // 결재요청 호출페이지 유형
          optValCtn2: isSetupRequest
            ? condition?.elmPrjId
            : JSON.stringify(
                pageId === ApproveRequestPageType.IP_APPROVE_REQ
                  ? ''
                  : pageId === ApproveRequestPageType.UT_REVIEW_REQ
                  ? { utmId: props.condition.utmId }
                  : [
                      ApproveRequestPageType.UT_LIB_REQ,
                      ApproveRequestPageType.UT_LIB_UPDATE_REQ,
                      ApproveRequestPageType.UT_LIB_DELETE_REQ,
                    ].includes(pageId)
                  ? ''
                  : condition
              ),
          optValCtn3: isSetupRequest ? condition?.excpId : '',
          bizReqData: childRef.current?.getBizReqData(detail.aprReqId, detail.trnmBdyCtn),
        });
        openLoading(false);
        if (response.successOrNot === 'Y') {
          openMessageBar({
            type: 'confirm',
            content: t('com.msg.결재요청되었습니다.', '결재요청되었습니다.'),
          });
          handleClose();
          return;
        }
        openMessageBar({
          type: 'error',
          content:
            response.data || t('com.msg.결재 요청에 실패했습니다.', '결재 요청에 실패했습니다.'),
        });
      },
    });
  };

  /**
   * 임시 저장 처리
   */
  const handleSave = async () => {
    // [25.10.31] 임시저장인 경우 validation 안하도록 변경
    // if (!(await validation())) return;
    const content = basicRef.current?.getReqContent();

    openCommonModal({
      modalType: 'confirm',
      content: t('com.label.저장하시겠습니까?', '저장하시겠습니까?'),
      yesCallback: async () => {
        openLoading(true);
        const aprReqTitNm =
          detail?.aprReqTitNm ||
          String(t('com.label.임시 저장된 결재 문서', '임시 저장된 결재 문서'));
        const response = await saveApproveRequest({
          ...detail,
          aprReqTitNm: aprReqTitNm,
          trnmBdyCtn: content,
          approvalAppd: approvalsRef.current,
          approvalInfr: notifiersRef.current,
          aprReqProgStatCd: 'TMP', // 임시저장
          optValCtn1: pageId, // // 결재요청 호출페이지 유형
          optValCtn2: isSetupRequest
            ? props?.condition?.elmPrjId
            : JSON.stringify(
                pageId === ApproveRequestPageType.IP_APPROVE_REQ
                  ? ''
                  : pageId === ApproveRequestPageType.UT_REVIEW_REQ
                  ? { utmId: props.condition.utmId }
                  : [
                      ApproveRequestPageType.UT_LIB_REQ,
                      ApproveRequestPageType.UT_LIB_UPDATE_REQ,
                      ApproveRequestPageType.UT_LIB_DELETE_REQ,
                    ].includes(pageId)
                  ? ''
                  : condition
              ),
          optValCtn3: isSetupRequest ? props?.condition?.excpId : '',
          bizReqData: childRef.current?.getBizReqData(detail.aprReqId, content),
        });
        openLoading(false);
        if (response.successOrNot === 'Y') {
          openMessageBar({
            type: 'confirm',
            content: t('com.label.저장되었습니다.', '저장되었습니다.'),
          });
          setDetail({
            ...detail,
            aprReqTitNm: aprReqTitNm,
            aprReqProgStatCd: 'TMP', // [25.11.23] 상태코드로 임시저장 상태 판단 후 삭제 버튼 노출하기 위해 추가
            aprReqProgStatNm: '임시저장',
          });
          return;
        }
        openMessageBar({
          type: 'error',
          content: response.data || t('com.msg.저장 실패했습니다.', '저장 실패했습니다.'),
        });
      },
    });
  };

  /**
   * 반려 상태인 결재 재작성 처리
   * [25.11.23] 반려인 경우 재작성 노출 > 클릭 시 결재정보 복사해서 신규 결재데이터 생성 (FA 일정등록쪽만 임시 적용)
   */
  const handleRewrite = async () => {
    openCommonModal({
      modalType: 'confirm',
      content: t(`com.msg.재작성하시겠습니까?`, `재작성하시겠습니까?`),
      yesCallback: async () => {
        openLoading(true);
        const response = await rewriteApprovalRequestResult(aprReqId);
        openLoading(false);

        if (response.successOrNot === 'Y') {
          openMessageBar({
            type: 'confirm',
            content: t('com.msg.생성되었습니다.', '생성되었습니다.'),
          });
          if (response.data) {
            setAprReqId(response.data);
            searchRequest(response.data);
          } else {
            handleClose();
          }
        } else {
          openMessageBar({
            type: 'error',
            content:
              typeof response.data === 'string'
                ? response.data
                : t('com.msg.오류가 발생하였습니다.', '오류가 발생하였습니다.'),
          });
        }
      },
    });
  };

  /**
   * 임시저장된 결재문서 삭제 처리
   * [25.11.23] 일단 FA 일정등록만 임시 적용
   */
  const handleRemove = async () => {
    openCommonModal({
      modalType: 'confirm',
      content: t(`com.msg.삭제하시겠습니까?`, `삭제하시겠습니까?`),
      yesCallback: async () => {
        openLoading(true);
        const response = await removeApprovalRequestResult(aprReqId);
        openLoading(false);

        if (response.successOrNot === 'Y') {
          openMessageBar({
            type: 'confirm',
            content: t('com.msg.삭제되었습니다.', '삭제되었습니다.'),
          });
          handleClose();
        } else {
          openMessageBar({
            type: 'error',
            content:
              typeof response.data === 'string'
                ? response.data
                : t('com.msg.오류가 발생하였습니다.', '오류가 발생하였습니다.'),
          });
        }
      },
    });
  };

  const handleChange = (name: string, value: string) => {
    if (name) {
      setDetail({ ...detail, [name]: value });
    }
  };

  const handleClose = () => {
    props.close();
  };

  const handleCancelApprove = () => {
    openCommonModal({
      modalType: 'confirm',
      content: t(`com.msg.결재취소하시겠습니까?`, `결재취소하시겠습니까?`),
      yesCallback: async () => {
        openLoading(true);
        const response = await cancelApprovalRequestResult(aprReqId);
        openLoading(false);

        if (response.successOrNot === 'Y') {
          openMessageBar({
            type: 'confirm',
            content: t('com.msg.결재취소되었습니다.', '결재취소되었습니다.'),
          });
          // [25.12.09] FA요청 결재취소인 경우 임시저장 상태로 변경 (현재 결재ID 유지)
          if (isSetupRequest) {
            searchRequest();
          } else {
            handleClose();
          }
        } else {
          openMessageBar({
            type: 'error',
            content:
              typeof response.data === 'string'
                ? response.data
                : t('com.msg.오류가 발생하였습니다.', '오류가 발생하였습니다.'),
          });
        }
      },
    });
  };

  const handleApprove = async (opinion: string) => {
    openLoading(true);
    const response = await approveApprovalRequestResult({
      ...detail,
      resultApprovalOpinion: opinion,
      resultAprLnSeq: waitApprovalAppd?.aprLnSeq ?? '',
      resultAprLnSnb: waitApprovalAppd?.aprLnSnb ?? '', // [고도화] 병렬 결재 추가로 인해 기존 seq = snb 전달되던거 변경함.
    } as ApproveRequestDetail);
    openLoading(false);

    if (response.successOrNot === 'Y') {
      openMessageBar({
        type: 'confirm',
        content: t('com.label.승인되었습니다.', '승인되었습니다.'),
      });
      handleClose();
    } else {
      openMessageBar({
        type: 'error',
        content:
          typeof response.data === 'string'
            ? response.data
            : t('com.label.요청에 실패했습니다.', '요청에 실패했습니다.'),
      });
    }
  };

  const handleRejectApprove = async (opinion: string) => {
    openLoading(true);
    const response = await rejectApprovalRequestResult({
      ...detail,
      resultApprovalOpinion: opinion,
      resultAprLnSeq: waitApprovalAppd?.aprLnSeq ?? '', // todo::: 우선 seq로 변경함. ip 결재가 .. 공통을 안씀.. 서비스단은 공통을 쓸거라서.. 일단 keep
      resultAprLnSnb: waitApprovalAppd?.aprLnSnb ?? '', // [고도화] 병렬 결재 추가로 인해 기존 seq = snb 전달되던거 변경함.
    } as ApproveRequestDetail);

    openLoading(false);
    if (response.successOrNot === 'Y') {
      openMessageBar({
        type: 'confirm',
        content: t('com.msg.반려 완료되었습니다.', '반려 완료되었습니다.'),
      });
      handleClose();
    } else {
      openMessageBar({
        type: 'error',
        content:
          typeof response.data === 'string'
            ? response.data
            : t('com.label.요청에 실패했습니다.', '요청에 실패했습니다.'),
      });
    }
  };

  // [고도화] 메일 공유 버튼
  const handleSendMailButton = () => {
    setConditionSendMail({
      aprReqId: detail?.aprReqId || '',
      aprReqTitNm: detail?.aprReqTitNm || '',
    });

    setOpenEmailModal(true);
  };

  const dialogButtons = useMemo(() => {
    if (!aprReqId || !detail?.aprReqId) return [];
    if (isNewApprove || 'TMP' === detail.aprReqProgStatCd) {
      let buttons = [] as ReactElement[];
      if (DELETE_ALLOWED_PAGE_IDS.includes(pageId) && detail?.aprReqProgStatCd === 'TMP') {
        // [25.11.23] 임시저장 상태인 경우 삭제 가능하도록 변경
        // 신규작성인 상태는 삭제 버튼 미노출 (aprReqProgStatCd = null)
        buttons = [
          <Button key={'remove'} onClick={handleRemove}>
            {t('com.button.삭제', '삭제')}
          </Button>,
        ];
      }
      return [
        ...buttons,
        <Button key={'save'} onClick={handleSave}>
          {t('com.button.임시저장', '임시저장')}
        </Button>,
        <Button
          key={'req'}
          css={IconButton.button}
          className="confirm"
          onClick={handleConfirm}
          disableRipple
        >
          {t('com.button.결재요청', '결재요청')}
        </Button>,
      ];
    }
    const buttons = [] as ReactElement[];
    if ('REQ' === detail.aprReqProgStatCd && detail.aprReqUserId === userId) {
      buttons.push(
        <Button key={'cancel'} className="cancel" onClick={handleCancelApprove} disableRipple>
          {t('com.button.상신취소', '결재취소')}
        </Button>
      );
    }
    if (['REQ', 'INP'].includes(detail?.aprReqProgStatCd || '') && isApprover) {
      buttons.push(
        <Button
          key={'reject'}
          className="cancel"
          onClick={() => {
            setApproveModalOpen('REJECT');
          }}
          disableRipple
        >
          {t('com.button.반려', '반려')}
        </Button>
      );
      buttons.push(
        <Button
          key={'approve'}
          css={IconButton.button}
          className="ok"
          onClick={() => {
            setApproveModalOpen('APPROVE');
          }}
          disableRipple
        >
          {t('com.button.승인', '승인')}
        </Button>
      );
    }
    // [25.11.23] 반려인 경우 재작성 노출 > 클릭 시 결재정보 복사해서 신규 결재데이터 생성
    if (
      REWRITE_ALLOWED_PAGE_IDS.includes(pageId) &&
      ['REJ'].includes(detail?.aprReqProgStatCd || '') &&
      detail.aprReqUserId === userId
    ) {
      buttons.push(
        <Button key={'rewrite'} onClick={handleRewrite}>
          {t('com.button.재작성', '재작성')}
        </Button>
      );
    }
    return buttons;
  }, [detail, isApprover, pageId]);

  return (
    <CustomDialog
      title={
        condition?.mode == 'READ' || !isEditable
          ? t('com.label.결재 정보', '결재 정보')
          : t('com.label.결재 요청', '결재 요청')
      }
      open={props.open}
      size={'xl'}
      buttons={dialogButtons}
      onClose={handleClose}
      onCancel={(dialogButtons || []).length ? handleClose : undefined}
    >
      <>
        {!['TMP', 'CCL', ''].includes(detail?.aprReqProgStatCd || '') && (
          <GlobalBtnGroup className="top">
            <Button
              css={IconButton.button}
              className="mail"
              onClick={handleSendMailButton}
              disableRipple
            >
              {t('com.button.메일 공유', '메일 공유')}
            </Button>
          </GlobalBtnGroup>
        )}
        {isEditable ? (
          <ApproveBasicInfo ref={basicRef} detail={detail} onChange={handleChange} />
        ) : (
          <ApproveDetailBasicInfo approveInfo={detail as ApproveRequestDetail} />
        )}

        <ApproveRequestDetailCase
          ref={childRef}
          pageId={pageId}
          condition={condition}
          aprReqId={aprReqId}
          isReadOnly={!isEditable}
          onChangePrj={setApprovalPrj}
          onChangeRole={setApprovalRole}
        />

        {isEditable ? (
          <ApprovalLineInfo
            approvalAppd={approvalsRef.current || []}
            approvalInfr={notifiersRef.current || []}
            onApprovalsChange={(list) => (approvalsRef.current = list)}
            onNotifiersChange={(list) => (notifiersRef.current = list)}
            pageId={pageId}
            approvalPrj={approvalPrj}
            approvalRole={approvalRole}
          />
        ) : (
          <ApproveDetailLineInfo
            pageId={pageId}
            approvalAppd={approveLine?.approvalAppd || []}
            approvalInfr={approveLine?.approvalInfr || []}
          />
        )}

        {approveModalOpen && (
          <ApproveModal
            open={approveModalOpen}
            title={t('com.label.결재의견', '결재의견')}
            approvalType={'0'}
            close={() => setApproveModalOpen(false)}
            approveCallback={(msg) => {
              setApproveModalOpen(false);
              handleApprove(msg);
            }}
            rejectCallback={(msg) => {
              setApproveModalOpen(false);
              handleRejectApprove(msg);
            }}
          />
        )}
        {isOpenEmailModal && (
          <ApproveSendEmailModal
            open={isOpenEmailModal}
            close={() => setOpenEmailModal(false)}
            condition={getConditionSendMail}
          />
        )}
      </>
    </CustomDialog>
  );
};

export default ApproveRequestModal;
